// The wizard, for somebody who has not signed in yet.
//
// A public route, so that "Start building" on the landing page goes straight
// into the product rather than into a signup form. That is the whole point of
// the page: a stranger can see what building a bot actually involves before
// deciding whether to hand over an email address.
//
// Once they do have an account there is no reason to keep a second copy of the
// wizard outside the dashboard, so they are handed to /agents/new. Nothing is
// lost in the move -- the wizard's answers live in sessionStorage (lib/draft),
// including the fact that they already pressed the button, which is what makes
// the agent get built on arrival instead of asking them to press it again.

import { Link, Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import Footer from "../components/Footer";
import { Logo } from "../components/Logo";
import { ColorModeToggle } from "../components/ColorModeToggle";
import StartupFailure from "../components/StartupFailure";
import { PRODUCT_NAME } from "../lib/brand";
import { LoadingPage } from "../components/ui";
import NewAgent from "./NewAgent";

export default function Start() {
  const { user, tenant, startupError } = useAuth();

  // Say so rather than spinning. The wizard itself would work without Firebase
  // -- nothing before the last step talks to a server -- but the last step is
  // signing up, so letting somebody fill in four steps here would be walking
  // them into a wall.
  if (startupError) return <StartupFailure message={startupError} />;

  // Wait rather than render. A signed-in customer who follows a /start link
  // should not see the signed-out wizard flash past on the way to the one
  // inside their dashboard.
  if (user === undefined) return <LoadingPage label="Loading…" />;
  if (user && tenant) return <Navigate to="/agents/new" replace />;

  // A brand-new user whose first tenant is still being created falls through to
  // the wizard on purpose: there is nothing for them to wait for while they are
  // still typing, and the redirect above fires the moment the tenant lands.
  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      <header className="border-b border-slate-200 bg-surface">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-4">
          <Link to="/" aria-label={PRODUCT_NAME} className="flex">
            <Logo />
          </Link>
          <div className="flex items-center gap-2">
            <ColorModeToggle />
            <Link
              to="/signin"
              className="text-sm font-medium text-slate-600 hover:text-slate-900"
            >
              Sign in
            </Link>
          </div>
        </div>
      </header>

      <main className="mx-auto w-full max-w-3xl flex-1 px-4 py-10">
        <NewAgent />
      </main>

      <Footer />
    </div>
  );
}
